/**
 * React Query hooks for Recall notetaker admin status management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { preferenceKey } from '@/hooks/integrations/recall/useRecallPreference';
import { apiGet, apiPut } from '@/lib/http/client';

export interface RecallAdminStatus {
  enabled: boolean;
  can_toggle: boolean;
}

export function useRecallAdminStatus(orgId: number | undefined) {
  return useQuery<RecallAdminStatus>({
    queryKey: ['recallAdminStatus', orgId],
    queryFn: () =>
      apiGet<RecallAdminStatus>(`/api/orgs/${orgId}/recall/admin/status/`),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

interface RecallAdminToggleBody {
  enabled: boolean;
}

export function useRecallAdminToggle(orgId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation<RecallAdminStatus, Error, RecallAdminToggleBody>({
    mutationFn: (body: RecallAdminToggleBody) => {
      if (!orgId) {
        return Promise.reject(new Error('Organization context not ready'));
      }
      return apiPut<RecallAdminStatus>(
        `/api/orgs/${orgId}/recall/admin/status/`,
        body
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recallAdminStatus', orgId] });
      queryClient.invalidateQueries({ queryKey: preferenceKey(orgId) });
    },
  });
}
